// v49.52 — Top-level ErrorBoundary.
// Catches React render-time errors anywhere in the tree, reports
// them via the centralised reporter, and shows a friendly fallback
// instead of a blank screen. Designed to sit at the very top of App.tsx
// so even the auth provider + protected shell are wrapped.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportBoundaryError } from "@/lib/errorReporter";
import { APP_VERSION } from "@/lib/version";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportBoundaryError(error, { componentStack: info.componentStack });
  }

  handleReload = () => {
    /* location.reload() rather than setState reset — we want to
       drop any in-memory state that may have caused the crash. */
    window.location.reload();
  };

  handleSignOut = () => {
    /* If the crash is auth-related, signing out is the safest recovery
       path. shellSignOut is exposed globally by features/auth/helpers
       (v48.306). Fall back to a reload + clear if missing. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const signOut = (window as any).shellSignOut;
    if (typeof signOut === "function") { void signOut(); return; }
    try { localStorage.clear(); } catch { /* ignore */ }
    window.location.assign("/");
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-lc-bg" style={{ fontFamily: "DM Sans, system-ui, sans-serif" }}>
        <div className="max-w-md w-full bg-white border border-lc-line rounded-lc-lg shadow-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-lc-line bg-lc-red-bg/40">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-lc-red-fg">Something broke</div>
            <div className="text-base font-extrabold text-lc-ink mt-0.5">The page hit an unexpected error</div>
          </div>
          <div className="px-6 py-5 flex flex-col gap-3">
            <p className="text-[13px] text-lc-mute leading-relaxed">
              We've automatically logged the error and the LabourCore team can pick it up. Most issues clear with a refresh.
              If the same crash happens again, signing out + back in usually resets things.
            </p>
            <pre className="text-[11px] text-lc-mute font-mono whitespace-pre-wrap bg-lc-bg-soft border border-lc-line rounded-lc-sm p-2.5 overflow-auto max-h-32">
              {this.state.error.name}: {this.state.error.message}
            </pre>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={this.handleSignOut}
                className="px-3 py-2 rounded-lc-md bg-white text-lc-ink-2 border border-lc-line text-sm font-extrabold hover:bg-lc-bg-2"
              >
                Sign out
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="px-3 py-2 rounded-lc-md bg-gradient-to-br from-lc-blue to-lc-blue-dark text-white text-sm font-extrabold"
              >
                Reload
              </button>
            </div>
            <div className="text-[10px] text-lc-faint font-mono text-center mt-2">{APP_VERSION}</div>
          </div>
        </div>
      </div>
    );
  }
}
